import { noop } from 'es-toolkit/compat';

import { LockOptions } from 'types';
import { core, safeToNumber } from '@tock/coretypes';
import AppConstants from 'constants/AppConstants';
import protos from 'utils/protos';

const { UnlockRequest, LockTicketsRequest, LockRequest, TicketSessionRelockRequest } = protos;

export function lockTickets(
  request: Partial<core.LockTicketsRequest>,
  lockDurationMinutes?: number,
  options?: LockOptions,
  businessId?: number,
  businessTimeZone?: string | null
): ApiAction<core.TicketGroup> {
  const updatedRequest = { ...request };

  if (request.seatingAreaId === 0) {
    delete updatedRequest.seatingAreaId;
  }

  const action: AnyApiAction = {
    putProto: '/ticket/group/lock',
    request: LockTicketsRequest.fromObject(updatedRequest),
    type: AppConstants.LOCK_TICKETS,
    context: {
      lockDurationMinutes,
      dateTimeString: request.dateTime,
      businessTimeZone,
    },
    scope: businessId
      ? {
          businessId: safeToNumber(businessId),
        }
      : undefined,
  };
  if (!options?.showError) {
    action.onError = noop;
    action.ignoreNetworkErrors = true;
  }
  return {
    TockApi: action,
  };
}

export function lockPackage(request: core.ILockRequest, lockDurationMinutes: number): ApiAction {
  return {
    TockApi: {
      putProto: '/ticket/lock',
      request: LockRequest.fromObject(request),
      type: AppConstants.LOCK_PACKAGE,
      context: {
        lockDurationMinutes,
        request,
      },
    },
  };
}

export function clear(): BaseTockAction {
  return {
    type: AppConstants.LOCK_CLEAR,
  };
}

export function clearPackageLock(): BaseTockAction {
  return {
    type: AppConstants.LOCK_CLEAR_PACKAGE,
  };
}

export function unlockTickets(request: core.IUnlockRequest, businessId?: number): ApiAction {
  return {
    TockApi: {
      putProto: '/ticket/unlock',
      request: UnlockRequest.fromObject(request),
      type: AppConstants.UNLOCK_TICKETS,
      onError: noop,
      ignoreNetworkErrors: true,
      scope: businessId
        ? {
            businessId: safeToNumber(businessId),
          }
        : undefined,
    },
  };
}

export function sessionRelockTickets(ticketSubsetIds: number[]): ApiAction {
  return {
    TockApi: {
      putProto: '/ticket/session/relock',
      request: TicketSessionRelockRequest.fromObject({
        ticketSubsetId: ticketSubsetIds,
      }),
      type: AppConstants.LOCK_SESSION_RELOCK_TICKETS,
      onError: noop,
      ignoreNetworkErrors: true,
    },
  };
}

export function fetchAll(businessId?: number): ApiAction {
  return {
    TockApi: {
      getProto: '/ticket/locks',
      type: AppConstants.LOCK_FETCH_ALL,
      scope: businessId
        ? {
            businessId: safeToNumber(businessId),
          }
        : undefined,
    },
  };
}

export function fetchAllByIds(ticketSubsetIds: string[]): ApiAction {
  return {
    TockApi: {
      getListProto: `/ticket/group/locks?ticket_subset_ids=${ticketSubsetIds.join(',')}`,
      type: AppConstants.LOCK_FETCH_ALL_BY_IDS,
    },
  };
}

export function selectNextLockInSequence(businessTimeZone: string): BaseTockAction {
  return {
    type: AppConstants.LOCK_SELECT_NEXT_IN_SEQUENCE,
    context: {
      businessTimeZone,
    },
  };
}

export function addCompletedPurchaseId(purchaseId: number): BaseTockAction {
  return {
    type: AppConstants.LOCK_ADD_COMPLETED_PURCHASE_ID,
    payload: purchaseId,
  };
}

export function fetchLockForBusiness(businessTimeZone: string, businessId: number): ApiAction {
  return {
    TockApi: {
      getProto: `/ticket/group/lock`,
      type: AppConstants.FETCH_BUSINESS_LOCK,
      scope: {
        businessId: safeToNumber(businessId),
      },
      context: {
        businessTimeZone,
      },
      onError: noop,
    },
  };
}

export function purchaseVerifyTokenBusiness(token: string): ApiAction {
  const action: AnyApiAction = {
    getProto: `/purchase/verify/business?token=${token}`,
    type: AppConstants.NULL,
  };
  return {
    TockApi: action,
  };
}

export function purchaseVerifyDecode(shortToken: string): ApiAction {
  const action: AnyApiAction = {
    getProto: `/purchase/verify/decode?shortToken=${shortToken}`,
    type: AppConstants.NULL,
  };
  return {
    TockApi: action,
  };
}
